// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { css, cx } from '@emotion/css';
import { Typography, useTheme } from '@mui/material';
import { BiSolidHeart } from 'react-icons/bi';

import GoogleMapsElement from './GoogleMapsElement';
import { LocationPinBubbleVisibilityContext } from './LocationPinMarker';
import { useMapSurfaceColors } from './useMapSurfaceColors';
import { getMarkerTitleColor, type LocationPinVariant } from './markerTheme';

// TYPES ---------------------------------------------------------------- //

interface LocationPinBubbleProps {
  markerId?: LocationPinVariant;
  title: string;
  name?: string;
  address?: string;
  position?: { lat: number; lng: number };
  favorited?: boolean;
  maxWidth?: number;
  pointerEvents?: 'none' | 'auto';
  children?: React.ReactNode;
}

// COMPONENT ---------------------------------------------------------------- //

/**
 * Detail bubble anchored above a `LocationPinMarker`. Title is colored by
 * `markerId` to match the pin; `name`, `address`, and `position` render as
 * primary/secondary/disabled text. Pass `children` for extra footer content
 * (e.g. a favorite indicator).
 */
const LocationPinBubble = ({
  markerId,
  title,
  name,
  address,
  position,
  favorited,
  maxWidth,
  pointerEvents,
  children,
}: LocationPinBubbleProps) => {
  const theme = useTheme();
  const surface = useMapSurfaceColors();
  const cls = useStyles();
  const visible = React.useContext(LocationPinBubbleVisibilityContext);

  const titleColor = getMarkerTitleColor(theme, markerId ?? '');

  return (
    <GoogleMapsElement
      className={cx(cls.bubble, visible ? cls.bubbleVisible : cls.bubbleHidden)}
      style={{
        zIndex: 9000,
        padding: 12,
        ...(maxWidth != null ? { maxWidth } : {}),
        ...(pointerEvents ? { pointerEvents } : {}),
      }}
    >
      <Typography
        sx={{
          marginBottom: `6px`,
          fontWeight: 600,
          color: titleColor,
        }}
      >
        {title}
      </Typography>
      {name != null && (
        <Typography variant='body2' sx={{ fontWeight: 500, color: surface.primary }}>
          {name}
        </Typography>
      )}
      {address != null && (
        <Typography variant='caption' sx={{ color: surface.secondary }}>
          {address}
        </Typography>
      )}
      {position && (
        <Typography sx={{ marginTop: `6px`, fontSize: `12px`, color: surface.disabled }}>
          {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
        </Typography>
      )}
      {favorited && (
        <div className={cls.favoriteIndicator}>
          <BiSolidHeart size={20} style={{ color: theme?.palette?.error?.main }} />
          <Typography variant='caption' sx={{ color: theme?.palette?.error?.main }}>
            Favorited
          </Typography>
        </div>
      )}
      {children}
    </GoogleMapsElement>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = () => ({
  bubble: css`
    top: 6px;
    left: 50%;
    width: max-content;
    transition:
      opacity 240ms ease-out,
      transform 240ms ease-out;
  `,
  bubbleVisible: css`
    opacity: 1;
    transform: translate(-50%, 0);
  `,
  bubbleHidden: css`
    opacity: 0;
    transform: translate(-50%, -4px);
    pointer-events: none;
  `,
  favoriteIndicator: css`
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 6px;
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default LocationPinBubble;
